import React, { useState, useRef, useEffect } from 'react';
import { API_BASE } from '../api';
import { useNavigate, Link } from 'react-router-dom';

export default function Register() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: '', phone: '', email: '', location: '', dob: '',
  });
  const [photo,        setPhoto]        = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [error,        setError]        = useState('');
  const [success,      setSuccess]      = useState('');
  const [loading,      setLoading]      = useState(false);
  const [camOpen,      setCamOpen]      = useState(false);

  const videoRef  = useRef();
  const canvasRef = useRef();
  const streamRef = useRef();

  useEffect(() => { setError(''); }, []);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  // Automatically compress image before upload (converts to JPEG ~300KB max)
  const compressImage = (file, maxWidth = 1000, quality = 0.7) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob((blob) => {
            if (!blob) {
              resolve(file);
              return;
            }
            resolve(new File([blob], file.name.replace(/\.[^/.]+$/, "") + ".jpg", { type: 'image/jpeg' }));
          }, 'image/jpeg', quality);
        };
      };
    });
  };

  const setPhotoFile = async (file) => {
    if (!file) return;
    // Compress if larger than 500 KB
    const finalFile = file.size > 500 * 1024 ? await compressImage(file) : file;
    
    setPhoto(finalFile);
    const reader = new FileReader();
    reader.onload = (e) => setPhotoPreview(e.target.result);
    reader.readAsDataURL(finalFile);
  };

  const openCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      streamRef.current = stream;
      setCamOpen(true);
    } catch (err) { alert('Camera error: ' + err.message); }
  };

  useEffect(() => {
    if (!camOpen) return;
    if (videoRef.current && streamRef.current) videoRef.current.srcObject = streamRef.current;
  }, [camOpen]);

  const capture = () => {
    const canvas = canvasRef.current, video = videoRef.current;
    if (!canvas || !video) return;
    canvas.width  = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    canvas.toBlob(blob => {
      if (!blob) return;
      setPhotoFile(new File([blob], 'photo-' + Date.now() + '.jpg', { type: 'image/jpeg' }));
      stopCamera();
    }, 'image/jpeg', 0.7);
  };

  const stopCamera = () => {
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    setCamOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!photo) { setError('Profile photo is required'); return; }
    if (photo.size > 3 * 1024 * 1024) { setError('Profile photo is too large. Please select an image under 3 MB.'); return; }

    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    fd.append('photo', photo);

    setLoading(true);
    try {
      const res  = await fetch(`${API_BASE}/api/manager/register`, { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok) { setError(data.message || 'Registration failed'); return; }
      setSuccess('✓ Registration successful! Redirecting to login…');
      setTimeout(() => navigate('/'), 2000);
    } catch { setError('Server error. Please try again.'); }
    finally { setLoading(false); }
  };

  return (
    <div className="auth-wrapper">
      <div className="auth-card" style={{ maxWidth: 520 }}>
        <div className="auth-logo">
          <img src="/logo-full.png" alt="Vegavruddhi Pvt. Ltd." />
          <span className="tagline">MANAGER PANEL</span>
        </div>
        <hr className="auth-divider" />
        <h2>Manager Registration 📝</h2>
        <p className="subtitle">Complete your registration to access the Manager portal.</p>

        {error   && <div className="error-msg"   style={{ display: 'block' }}>{error}</div>}
        {success && <div className="success-msg" style={{ display: 'block' }}>{success}</div>}

        <form onSubmit={handleSubmit}>
          {/* Photo upload */}
          <div className="form-group">
            <label>Profile Photo <span className="req">*</span></label>
            <div className="photo-upload-box">
              <div className="photo-preview-wrap">
                <div
                  className="photo-preview"
                  style={photoPreview ? {
                    backgroundImage:    `url(${photoPreview})`,
                    backgroundSize:     'cover',
                    backgroundPosition: 'center',
                  } : {}}
                >
                  {!photoPreview && '👤'}
                </div>
              </div>
              <div className="photo-actions">
                <button type="button" className="photo-btn" onClick={openCamera}>
                  📷 Take Photo
                </button>
                <label className="photo-btn">
                  🖼 Choose from Gallery
                  <input
                    type="file"
                    accept="image/*"
                    onChange={e => e.target.files[0] && setPhotoFile(e.target.files[0])}
                  />
                </label>
              </div>
              <div className="file-name">{photo ? photo.name : 'No photo selected'}</div>
            </div>
          </div>

          {/* Name + Location */}
          <div className="form-row">
            <div className="form-group">
              <label>Full Name <span className="req">*</span></label>
              <input
                type="text"
                value={form.name}
                onChange={set('name')}
                placeholder="Full name"
                required
              />
            </div>
            <div className="form-group">
              <label>Location <span className="req">*</span></label>
              <input
                type="text"
                value={form.location}
                onChange={set('location')}
                placeholder="City / Office"
                required
              />
            </div>
          </div>

          {/* Phone + Email */}
          <div className="form-row">
            <div className="form-group">
              <label>Phone Number <span className="req">*</span></label>
              <input
                type="tel"
                value={form.phone}
                onChange={set('phone')}
                placeholder="+91 XXXXX XXXXX"
                maxLength={10}
                pattern="[0-9]{10}"
                title="Please enter a 10-digit phone number"
                required
              />
            </div>
            <div className="form-group">
              <label>Email ID <span className="req">*</span></label>
              <input
                type="email"
                value={form.email}
                onChange={set('email')}
                placeholder="manager@email.com"
                required
              />
            </div>
          </div>

          {/* Date of Birth */}
          <div className="form-group">
            <label>Date of Birth <span className="req">*</span></label>
            <input
              type="date"
              value={form.dob}
              onChange={set('dob')}
              required
              max={new Date().toISOString().split('T')[0]}
            />
          </div>

          {/* Position (Read-only) */}
          <div className="form-group">
            <label>Position <span className="req">*</span></label>
            <input
              type="text"
              value="Manager"
              readOnly
              style={{
                background: '#f0f5f0',
                cursor: 'not-allowed',
                color: 'var(--green-dark)',
                fontWeight: 600,
              }}
            />
          </div>

          <button type="submit" className="btn" disabled={loading}>
            {loading ? 'Submitting…' : 'Submit Registration'}
          </button>
        </form>

        <div className="auth-link">
          Already registered? <Link to="/" className="register-link">Sign in here</Link>
        </div>
      </div>

      {/* Camera modal */}
      {camOpen && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
          zIndex: 9999, display: 'flex', alignItems: 'center',
          justifyContent: 'center', flexDirection: 'column', gap: 16,
        }}>
          <video ref={videoRef} autoPlay playsInline style={{ width: '100%', maxWidth: 420, borderRadius: 12 }} />
          <div style={{ display: 'flex', gap: 12 }}>
            <button
              onClick={capture}
              style={{ padding: '12px 28px', background: '#1a4731', color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}
            >
              📷 Capture
            </button>
            <button
              onClick={stopCamera}
              style={{ padding: '12px 28px', background: '#555', color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, cursor: 'pointer' }}
            >
              Cancel
            </button>
          </div>
          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </div>
      )}
    </div>
  );
}
